import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, formatCOP } from '../services/api';
import { pedidoClienteLabel } from '../utils/textEncoding';
import { formatColombia } from '../utils/colombiaTime';

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_preparacion: 'En preparación',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

const FILTROS = [
  { value: 'pendiente', label: 'Pendientes' },
  { value: '', label: 'Todos' },
  { value: 'confirmado', label: 'Confirmados' },
  { value: 'en_preparacion', label: 'En preparación' },
  { value: 'entregado', label: 'Entregados' },
  { value: 'cancelado', label: 'Cancelados' },
];

const HORAS_URGENTE = 2;

function estadoClass(estado) {
  return `estado-badge estado-${estado}`;
}

function esUrgente(p) {
  if (p.estado !== 'pendiente') return false;
  const age = Date.now() - new Date(p.created_at).getTime();
  return age > HORAS_URGENTE * 60 * 60 * 1000;
}

export default function PedidosInbox({ pin, onSelect, selectedId, refreshKey = 0 }) {
  const [filtro, setFiltro] = useState('pendiente');
  const [vendedorId, setVendedorId] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [q, setQ] = useState('');
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const loadSeq = React.useRef(0);

  useEffect(() => {
    let cancelled = false;
    api
      .getVendedores()
      .then((r) => {
        if (!cancelled) setVendedores(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setVendedores([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setError('');
    try {
      const res = await api.getPedidos(
        {
          estado: filtro || undefined,
          q: q.trim() || undefined,
          vendedor_id: vendedorId || undefined,
        }
      );
      if (seq !== loadSeq.current) return;
      setPedidos(res.data || []);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError(err.message || 'No se pudieron cargar los pedidos');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [pin, filtro, q, vendedorId]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 45000);
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    const onOnline = () => load();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
    };
  }, [load]);

  useEffect(() => {
    if (refreshKey > 0) load();
  }, [refreshKey, load]);

  const pendientes = pedidos.filter((p) => p.estado === 'pendiente').length;
  const urgentes = pedidos.filter(esUrgente).length;

  const vendedorNombre = useMemo(() => {
    if (!vendedorId) return '';
    const v = vendedores.find((x) => String(x.id) === String(vendedorId));
    return v?.nombre || '';
  }, [vendedorId, vendedores]);

  return (
    <div className="inbox">
      <header className="panel-head">
        <div>
          <h2>Pedidos recibidos</h2>
        </div>
        {filtro === 'pendiente' && pendientes > 0 ? (
          <span className="badge-count">{pendientes} por revisar</span>
        ) : null}
      </header>

      {urgentes > 0 && filtro === 'pendiente' ? (
        <p className="urgente-banner">
          {urgentes} pedido{urgentes !== 1 ? 's' : ''} esperando más de {HORAS_URGENTE}h
        </p>
      ) : null}

      <div className="toolbar">
        <input
          type="search"
          placeholder="Buscar número, cliente, vendedor..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <button type="button" onClick={load}>
          Buscar
        </button>
        <button type="button" className="btn-ghost" onClick={load} title="Actualizar">
          ↻
        </button>
      </div>

      <div className="vendedor-filter">
        <label htmlFor="filtro-vendedor">Vendedor</label>
        <select
          id="filtro-vendedor"
          value={vendedorId}
          onChange={(e) => setVendedorId(e.target.value)}
        >
          <option value="">Todos los vendedores</option>
          {vendedores.map((v) => (
            <option key={v.id} value={v.id}>
              {v.nombre}
            </option>
          ))}
        </select>
        {vendedorId ? (
          <button type="button" className="btn-clear-filter" onClick={() => setVendedorId('')}>
            Limpiar
          </button>
        ) : null}
      </div>

      <div className="filtros">
        {FILTROS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            className={`chip ${filtro === f.value ? 'active' : ''}`}
            onClick={() => setFiltro(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error ? <p className="error banner inbox-error">{error}</p> : null}

      {loading ? (
        <p className="muted center">Cargando pedidos...</p>
      ) : pedidos.length === 0 && !error ? (
        <p className="empty center">
          No hay pedidos
          {filtro === 'pendiente'
            ? ' pendientes'
            : filtro
              ? ` en estado “${ESTADO_LABEL[filtro] || filtro}”`
              : ''}
          {vendedorNombre ? ` de ${vendedorNombre}` : ''}.
        </p>
      ) : (
        <ul className="pedido-list">
          {pedidos.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className={`pedido-row ${selectedId === p.id ? 'selected' : ''} ${esUrgente(p) ? 'urgente' : ''}`}
                onClick={() => onSelect(p.id)}
              >
                <div className="pedido-row-main">
                  <strong>{p.numero}</strong>
                  <span className={estadoClass(p.estado)}>{ESTADO_LABEL[p.estado] || p.estado}</span>
                </div>
                <div className="pedido-row-sub">
                  <span>{pedidoClienteLabel(p)}</span>
                  {p.es_sede || p.padre_codigo ? (
                    <span className="origen-pill"> · Sucursal</span>
                  ) : null}
                  {p.origen === 'web' ? (
                    <span className="origen-pill origen-web"> · Tienda web</span>
                  ) : p.vendedor ? (
                    <span className="pedido-vendedor"> · {p.vendedor}</span>
                  ) : null}
                </div>
                {esUrgente(p) ? <p className="pedido-urgente-tag">Esperando hace rato</p> : null}
                <div className="pedido-row-meta">
                  <span>{formatCOP(p.subtotal)}</span>
                  <span>
                    {formatColombia(p.created_at, {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
